import React, { useState, useEffect } from 'react';
import Navbar from '../../components/navbar/navbar';
import FooterTop from '../../components/footer-top';
import Footer from '../../components/footer';
import api from '../../utils/api';
import PackageInquiryModal from '../../components/modals/PackageInquiryModal';

const dummyData = [
  { 
    name: 'Starter', 
    color: '#22c55e', 
    icon: 'fa-leaf', 
    price: '999', 
    period: '30 Days',
    tagline: 'Ideal for individual brokers',
    categories: [
      {
        title: 'Listings System (Core)',
        features: ['10 Active Listings', '1 Property Type', '2 BHK Types', '30 Days Validity']
      },
      {
        title: 'Media Features',
        features: ['5 Images per Listing', 'No YouTube Link', 'No Floor Plans', 'No brochure PDF']
      },
      {
        title: 'Location Features',
        features: ['Google Map View', 'No Exact Pin Location']
      },
      {
        title: 'Visibility & Boost',
        features: ['No Featured Listings', 'Low Search Priority', 'No Hot Property Tag']
      },
      {
        title: 'Leads System',
        features: ['Inquiry Leads enabled', 'WhatsApp Button', 'No Email Alerts', 'Basic Lead Dashboard']
      },
      {
        title: 'Broker Dashboard',
        features: ['Add/Edit Listings', 'Basic Lead Management', 'No Profile Page', 'No Branding']
      }
    ]
  },
  { 
    name: 'Growth', 
    color: '#3b82f6', 
    icon: 'fa-star', 
    price: '2,999', 
    period: '30 Days',
    tagline: 'Step up your broker status',
    popular: true,
    categories: [
      {
        title: 'Listings System (Core)',
        features: ['25 Active Listings', '2 Property Types', '4 BHK Types', '30 Days Validity']
      },
      {
        title: 'Media Features',
        features: ['10 Images per Listing', '1 YouTube Video Link', 'No Floor Plans', 'No brochure PDF']
      },
      {
        title: 'Location Features',
        features: ['Google Map View', 'Exact Pin Location Enabled']
      },
      {
        title: 'Visibility & Boost',
        features: ['5 Featured Listings', '7 Days Featured Duration', 'Medium Search Priority']
      },
      {
        title: 'Leads System',
        features: ['Inquiry Leads enabled', 'WhatsApp Button', 'Email Alerts enabled', 'Standard Dashboard']
      },
      {
        title: 'Broker Dashboard',
        features: ['Add/Edit Listings', 'Standard Lead Management', 'Public Profile Page', 'Branding (Logo + Details)']
      }
    ]
  },
  { 
    name: 'Pro', 
    color: '#8b5cf6', 
    icon: 'fa-fire', 
    price: '6,999', 
    period: '30 Days',
    tagline: 'Maximum power for pros',
    categories: [
      {
        title: 'Listings System (Core)',
        features: ['75 Active Listings', '4 Property Types', '8 BHK Types', '30 Days Validity']
      },
      {
        title: 'Media Features',
        features: ['20 Images per Listing', '2 YouTube Video Links', 'Floor Plan Upload', 'Brochure PDF Support']
      },
      {
        title: 'Location Features',
        features: ['Google Map View', 'Exact Pin Location Enabled']
      },
      {
        title: 'Visibility & Boost',
        features: ['15 Featured Listings', '10 Days Featured Duration', 'High Search Priority', 'Hot Property Tag']
      },
      {
        title: 'Leads System',
        features: ['Inquiry Leads enabled', 'WhatsApp Button', 'Email Alerts enabled', 'Advanced CRM Dashboard', 'Lead Analytics']
      },
      {
        title: 'Broker Dashboard',
        features: ['Add/Edit Listings', 'Advanced Lead Management', 'Public Profile Page', 'Branding (Logo + Details)']
      }
    ]
  },
  { 
    name: 'Enterprise', 
    color: '#ef4444', 
    icon: 'fa-rocket', 
    price: '14,999', 
    period: '30 Days',
    tagline: 'The ultimate agency suite',
    categories: [
      {
        title: 'Listings System (Core)',
        features: ['Unlimited Listings', 'Unlimited Types', 'Unlimited BHK Types', '30 Days Validity']
      },
      {
        title: 'Media Features',
        features: ['50 Images per Listing', 'Unlimited YouTube Links', 'Floor Plan Upload', 'Brochure PDF Support']
      },
      {
        title: 'Location Features',
        features: ['Google Map View', 'Exact Pin Location Enabled']
      },
      {
        title: 'Visibility & Boost',
        features: ['Unlimited Featured', '15 Days Featured Duration', 'Highest Search Priority', 'Hot Property Tag']
      },
      {
        title: 'Leads System',
        features: ['Inquiry Leads enabled', 'WhatsApp Button', 'Email Alerts enabled', 'Full CRM Suite', 'Lead Analytics']
      },
      {
        title: 'Broker Dashboard',
        features: ['Add/Edit Listings', 'Full Lead Management', 'Public Profile Page', 'Branding', 'Multi-User Access']
      }
    ]
  },
];

export default function BrokerPackages() {
  const [packages, setPackages] = useState(dummyData);
  const [loading, setLoading] = useState(true);
  const [selectedPackage, setSelectedPackage] = useState<{ name: string; type: string } | null>(null);

  useEffect(() => {
    const fetchPackages = async () => {
      try {
        const data = await api.getPackages('broker');
        if (data && data.length > 0) {
          setPackages(data);
        }
      } catch (err) {
        console.error('Failed to fetch dynamic broker packages, using dummy data.');
      } finally {
        setLoading(false);
      }
    };
    fetchPackages();
  }, []);

  const cleanTitle = (title: string) => {
    let cleaned = title.replace(/[\uD800-\uDBFF][\uDC00-\uDFFF]|\u2705|\u274C/g, '').trim();
    const mapping: Record<string, string> = {
      'LISTINGS SYSTEM': 'CORE',
      'MEDIA SYSTEM': 'DISPLAY',
      'LOCATION + SEARCH': 'LOCALITY',
      'VISIBILITY SYSTEM': 'BOOST',
      'LEAD MGMT': 'ENGAGE',
      'ADVANCED FEATURES': 'PREMIUM'
    };
    for (const [key, value] of Object.entries(mapping)) {
      if (cleaned.toUpperCase().includes(key)) return value;
    }
    return cleaned.replace(/^\d+\.\s*/, '').replace(/\s*\(.*\)$/, '').trim().toUpperCase();
  };

  const cleanFeature = (text: string) => {
    return text.replace(/[\uD800-\uDBFF][\uDC00-\uDFFF]|\u2705|\u274C/g, '').trim();
  };

  return (
    <div className="bg-white min-h-screen font-sans overflow-x-hidden">
      <Navbar transparent={false} />

      <PackageInquiryModal 
        isOpen={!!selectedPackage}
        onClose={() => setSelectedPackage(null)}
        packageName={selectedPackage?.name || ''}
        packageType="Broker"
      />

      {/* Hero Section */}
      <section className="relative pt-48 pb-32 overflow-hidden bg-blue-950">
        <div className="absolute inset-0 z-0 overflow-hidden">
           <div className="absolute -top-[20%] -right-[10%] w-[60%] h-[60%] bg-[#D4A017] opacity-[0.05] rounded-full blur-[120px]"></div>
           <div className="absolute -bottom-[20%] -left-[10%] w-[60%] h-[60%] bg-[#D4A017] opacity-[0.05] rounded-full blur-[120px]"></div>
        </div>
        
        <div className="container relative z-10 mx-auto px-6 text-center">
          <div className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-white/5 border border-white/10 text-blue-400 text-[10px] font-black uppercase tracking-[0.4em] mb-8 shadow-2xl backdrop-blur-md">
            <i className="fa-solid fa-users opacity-70"></i>
            Broker Partnership Ecosystem
          </div>
          <h1 className="text-5xl md:text-7xl font-black text-white mb-6 tracking-tight leading-tight uppercase italic">
            Accelerate <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-blue-600">Your Sales</span>
          </h1>
          <p className="max-w-2xl mx-auto text-blue-100/40 text-xs font-black uppercase tracking-[4px] leading-relaxed">
            High-performance listing packages designed for individual brokers and large agencies. Get the visibility you deserve.
          </p>
        </div>
      </section>

      {/* Pricing Cards Section */}
      <section className="relative z-20 -mt-10 pb-32">
        <div className="max-w-7xl mx-auto px-4">
          {loading ? (
             <div className="flex justify-center items-center py-20">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#D4A017]"></div>
             </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-10 max-w-5xl mx-auto">
              {packages.map((pkg, idx) => (
                <div 
                  key={pkg.name} 
                  className={`group relative flex flex-col bg-white rounded-2xl border transition-all duration-700 hover:-translate-y-4 overflow-hidden shadow-[0_30px_60px_-15px_rgba(0,0,0,0.1)] hover:shadow-[0_50px_100px_-20px_rgba(30,58,138,0.2)] ${pkg.popular ? 'border-blue-500/30 ring-4 ring-blue-500/5' : 'border-blue-900/5'}`}
                >
                  {/* Decorative Gradient Overlay */}
                  <div className="absolute top-0 right-0 w-64 h-64 bg-blue-500/5 rounded-full blur-3xl -z-10 group-hover:bg-blue-500/10 transition-all duration-700"></div>

                  {/* Standard/Popular Tag */}
                  {pkg.popular && (
                    <div className="absolute top-8 right-[-50px] bg-gradient-to-r from-blue-600 to-blue-800 text-white text-[10px] font-bold py-2.5 px-14 rotate-45 uppercase tracking-[0.3em] shadow-2xl z-20">
                      Standard
                    </div>
                  )}

                  {/* Header Area */}
                  <div className="p-10 bg-blue-950 text-white relative">
                    <div className="absolute inset-0 bg-gradient-to-br from-blue-900/50 to-transparent"></div>
                    <div className="relative z-10">
                      <div className="w-16 h-16 rounded-2xl flex items-center justify-center text-2xl shadow-2xl mb-8 group-hover:scale-110 transition-transform duration-500 bg-white/10 border border-white/20 backdrop-blur-md" style={{ color: pkg.color }}>
                        <i className={`fa-solid ${pkg.icon}`}></i>
                      </div>
                      
                      <h3 className="text-3xl font-bold tracking-tight mb-2 text-white">{pkg.name}</h3>
                      <p className="text-[10px] font-medium text-blue-200/50 uppercase tracking-[4px] mb-8">{pkg.tagline}</p>
                      
                      <div className="flex items-baseline gap-2">
                        <span className="text-6xl font-bold tracking-tighter text-white">
                          ₹{pkg.price}
                        </span>
                        <span className="text-xs font-medium text-blue-200/30 uppercase tracking-[3px]">
                          / {pkg.period}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Features Area */}
                  <div className="p-10 flex-grow bg-white">
                    <div className="space-y-12">
                      {pkg.categories.map((cat, ci) => (
                        <div key={ci} className="relative">
                          <h4 className="text-[11px] font-bold text-blue-950 uppercase tracking-[4px] mb-6 flex items-center gap-3">
                             <span className="w-1.5 h-1.5 rounded-full bg-blue-600 shadow-[0_0_10px_rgba(37,99,235,0.5)]"></span>
                             {cleanTitle(cat.title)}
                          </h4>
                          <ul className="space-y-2 px-2">
                            {cat.features.map((feat, fi) => {
                              const isAvailable = !feat.includes('❌') && !feat.toLowerCase().includes('no ');
                              const cleanText = cleanFeature(feat);
                              return (
                                <li key={fi} className={`flex items-start gap-4 transition-all ${isAvailable ? 'text-blue-950' : 'opacity-20 grayscale'}`}>
                                  <div className={`mt-1 flex-shrink-0 w-5 h-5 rounded-md flex items-center justify-center border ${isAvailable ? 'bg-blue-50 border-blue-100 text-blue-600' : 'border-gray-100 text-gray-300'}`}>
                                    <i className={`fa-solid ${isAvailable ? 'fa-check' : 'fa-xmark'} text-[10px]`}></i>
                                  </div>
                                  <span className="text-[15px] font-medium leading-relaxed">
                                    {cleanText}
                                  </span>
                                </li>
                              );
                            })}
                          </ul>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Footer Area */}
                  <div className="p-10 bg-gray-50/50 border-t border-gray-100">
                    <button 
                      onClick={() => setSelectedPackage({ name: pkg.name, type: 'broker' })}
                      className={`w-full py-6 rounded-2xl text-xs font-bold uppercase tracking-[4px] transition-all shadow-2xl active:scale-95 transform hover:scale-[1.02] relative overflow-hidden group/btn ${pkg.popular ? 'bg-blue-600 text-white hover:bg-blue-700' : 'bg-blue-950 text-white hover:bg-black'}`}
                    >
                      <div className="absolute inset-0 bg-white/10 -translate-x-full group-hover/btn:translate-x-0 transition-transform duration-500"></div>
                      <span className="relative z-10">Initialize Plan</span>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Agency Call to Action */}
      <div className="mt-40 max-w-5xl mx-auto text-center pb-40 px-6">
        <div className="p-16 rounded-[3rem] bg-blue-950 text-white relative overflow-hidden shadow-[0_50px_100px_-20px_rgba(2,6,23,0.5)] group/cta">
          <div className="absolute top-0 right-0 w-96 h-96 bg-blue-600/10 blur-[120px] rounded-full group-hover/cta:scale-150 transition-transform duration-1000"></div>
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-blue-400/5 blur-[120px] rounded-full group-hover/cta:scale-150 transition-transform duration-1000"></div>
          
          <div className="relative z-10">
            <h2 className="text-4xl md:text-6xl font-black mb-8 uppercase tracking-tighter italic">Large Agency?</h2>
            <p className="text-blue-100/40 text-[10px] font-black uppercase tracking-[5px] mb-12 max-w-2xl mx-auto leading-relaxed">
              Get custom plans for multi-agent teams with shared inventory and centralized lead management protocols.
            </p>
            <div className="flex flex-col sm:flex-row gap-6 justify-center">
              <button className="px-12 py-6 bg-blue-600 text-white rounded-xl font-black uppercase tracking-[4px] text-[11px] hover:bg-blue-500 transition-all shadow-2xl shadow-blue-500/40 active:scale-95">
                Request Agency Quote
              </button>
              <button className="px-12 py-6 bg-white/5 border border-white/10 backdrop-blur-xl text-white rounded-xl font-black uppercase tracking-[4px] text-[11px] hover:bg-white/10 transition-all active:scale-95">
                View CRM Protocols
              </button>
            </div>
          </div>
        </div>
      </div>

      <FooterTop bg="bg-white" />
      <Footer />
    </div>
  );
}
